import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Form,
  Input,
  Button,
  Descriptions,
  Tag,
  Divider,
  message,
  Space,
  Spin,
  Timeline,
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import FileUpload from "../../components/FileUpload";
import type { Case, Attachment } from "../../../shared/types";
import { api } from "../../services/api";
import dayjs from "dayjs";

const { TextArea } = Input;

const statusMap = {
  draft: { color: "default", text: "草稿" },
  pending: { color: "processing", text: "待处理" },
  archived: { color: "success", text: "已归档" },
};

export default function CaseHandlePage() {
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCase();
  }, [id]);

  const fetchCase = async () => {
    try {
      const data = await api.getCase(Number(id));
      setCaseData(data);
      setAttachments(
        (data.attachments || []).filter(
          (a) => a.category === "handle_file"
        )
      );
    } catch (error: any) {
      message.error(error.message || "获取案件信息失败");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      // 添加办理记录
      await api.addRecord(Number(id), "handle", values.handleContent);

      // 归档案件
      await api.archiveCase(Number(id));

      message.success("案件已办理完成并归档");
      navigate("/");
    } catch (error: any) {
      message.error(error.message || "办理失败");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (!caseData) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <Card bordered={false}>
        <div className="mb-6">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/")}
            className="mb-4"
          >
            返回列表
          </Button>
          <h2 className="text-xl font-semibold">案件办理</h2>
        </div>

        <Descriptions
          title="案件基本信息"
          bordered
          column={2}
          className="mb-6"
        >
          <Descriptions.Item label="案件编号">
            <span className="font-mono">{caseData.case_no}</span>
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={statusMap[caseData.status]?.color}>
              {statusMap[caseData.status]?.text}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="案件名称" span={2}>
            {caseData.case_name}
          </Descriptions.Item>
          <Descriptions.Item label="创建人">
            {caseData.creator_name}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {dayjs(caseData.created_at).format("YYYY-MM-DD HH:mm")}
          </Descriptions.Item>
        </Descriptions>

        {caseData.case_situation && (
          <Card
            type="inner"
            title="案件情况"
            className="mb-4"
            size="small"
          >
            <p className="whitespace-pre-wrap">{caseData.case_situation}</p>
          </Card>
        )}

        {caseData.resolution_info && (
          <Card
            type="inner"
            title="矛盾化解情况"
            className="mb-4"
            size="small"
          >
            <p className="whitespace-pre-wrap">{caseData.resolution_info}</p>
          </Card>
        )}

        {caseData.intervention_info && (
          <Card
            type="inner"
            title="介入情况"
            className="mb-4"
            size="small"
          >
            <p className="whitespace-pre-wrap">
              {caseData.intervention_info}
            </p>
          </Card>
        )}

        {caseData.records && caseData.records.length > 0 && (
          <Card type="inner" title="操作记录" className="mb-4" size="small">
            <Timeline
              items={caseData.records.map((record) => ({
                children: (
                  <div>
                    <div className="font-medium">{record.user_name}</div>
                    <div className="text-gray-500 text-sm">
                      {dayjs(record.created_at).format("YYYY-MM-DD HH:mm")}
                    </div>
                    <div className="mt-1">{record.content}</div>
                  </div>
                ),
              }))}
            />
          </Card>
        )}

        <Divider />

        <h3 className="text-lg font-medium mb-4">办理信息</h3>
        <Form form={form} layout="vertical" size="large">
          <Form.Item
            name="handleContent"
            label="办理情况"
            rules={[{ required: true, message: "请输入办理情况" }]}
          >
            <TextArea
              placeholder="请输入办理情况说明"
              rows={6}
              showCount
              maxLength={5000}
            />
          </Form.Item>

          <Form.Item label="附件">
            <FileUpload
              caseId={Number(id)}
              category="handle_file"
              value={attachments}
              onChange={setAttachments}
              maxCount={10}
            />
          </Form.Item>
        </Form>

        <Divider />

        <div className="flex justify-end">
          <Space>
            <Button onClick={() => navigate("/")}>取消</Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={handleSubmit}
              loading={saving}
            >
              完成办理并归档
            </Button>
          </Space>
        </div>
      </Card>
    </div>
  );
}
